import type { CartItem } from '@/features/cart/service/use-cart';

import React from 'react';

import { dateToApiDate } from '@/core/features/date/date';
import { triggerAppClearCart } from '@/core/features/native-app-bridge/native-app-bridge-service';
import { atom, useAtomValue, useSetAtom } from '@/core/features/store/atom-store';
import LocalStorage from '@/core/features/store/local-storage';
import { TIME_1H } from '@/core/utils/time';

type LocalStorageCartItem = {
    count: number;
    endDate: string;
    offerId: string;
    productId: string;
    startDate: string;
};

type LocalStorageCart = {
    cartItems: LocalStorageCartItem[];
    wasTrackedUntil: {
        cart: null | number;
        checkout: null | number;
        summary: null | number;
    };
};

type UseCartLocalStorageProps = {
    activitySpotId: null | number | string;
    schoolId: null | number | string;
};

const initialCart: LocalStorageCart = {
    cartItems: [],
    wasTrackedUntil: {
        cart: null,
        checkout: null,
        summary: null,
    },
};

const CART_TTL_HOURS = 20;
const CART_TTL = 60 * 60 * CART_TTL_HOURS; // 20h in s
const TRACKING_TTL = {
    cart: TIME_1H * CART_TTL_HOURS, // 20h in ms
    checkout: TIME_1H * CART_TTL_HOURS, // 20h in ms
    summary: TIME_1H * 2, // 2h in ms
};
const cartLocalStorage = new LocalStorage<LocalStorageCart>('cart', { defaultTTL: CART_TTL });
const cartLocalStorageAtom = atom<LocalStorageCart>(initialCart);

const hydrateCartLocalStorageAtom = atom(null, (_get, set, storageKey: string) => {
    const storedCart = cartLocalStorage.getKey(storageKey) ?? initialCart;
    set(cartLocalStorageAtom, storedCart);
    return storedCart;
});

const saveCartLocalStorageAtom = atom(
    null,
    (get, set, { cart, storageKey }: { cart: LocalStorageCart; storageKey: string }) => {
        cartLocalStorage.setKey(storageKey, cart);
        set(cartLocalStorageAtom, cart);
        return cart;
    },
);

const clearCartLocalStorageAtom = atom(null, (get, set, storageKey: string) => {
    cartLocalStorage.removeItem(storageKey);
    set(cartLocalStorageAtom, initialCart);
    return null;
});

export function useCartLocalStorage({ activitySpotId, schoolId }: UseCartLocalStorageProps) {
    const cartLocalStorageAtomState = useAtomValue(cartLocalStorageAtom);
    const hydrateCartLocalStorageAtomState = useSetAtom(hydrateCartLocalStorageAtom);
    const saveCartLocalStorageAtomState = useSetAtom(saveCartLocalStorageAtom);
    const clearCartLocalStorageAtomState = useSetAtom(clearCartLocalStorageAtom);
    const [isCartReady, setIsCartReady] = React.useState<boolean>(false);
    const storageKey = `${activitySpotId}_${schoolId}`;

    React.useEffect(() => {
        hydrateCartLocalStorageAtomState(storageKey);
        setIsCartReady(true);
    }, [hydrateCartLocalStorageAtomState, storageKey]);

    const isProductInCart = React.useCallback(
        (productId: string): boolean =>
            cartLocalStorageAtomState?.cartItems.some((item) => item.productId === productId) ?? false,
        [cartLocalStorageAtomState],
    );

    const saveCartItems = React.useCallback(
        (cartItems: CartItem[]) => {
            const updatedLocalStorageCartItems = cartItems.map<LocalStorageCartItem>((item) => ({
                count: item.count.current,
                endDate: dateToApiDate(item.endDate),
                offerId: item.offerId,
                productId: item.productId,
                startDate: dateToApiDate(item.startDate),
            }));
            saveCartLocalStorageAtomState({
                cart: {
                    cartItems: updatedLocalStorageCartItems,
                    wasTrackedUntil: cartLocalStorageAtomState.wasTrackedUntil,
                },
                storageKey,
            });
        },
        [cartLocalStorageAtomState.wasTrackedUntil, saveCartLocalStorageAtomState, storageKey],
    );

    const markCartAsTracked = React.useCallback(() => {
        const updatedLocalStorageCart: LocalStorageCart = {
            ...cartLocalStorageAtomState,
            wasTrackedUntil: {
                cart: Date.now() + TRACKING_TTL.cart,
                checkout: cartLocalStorageAtomState.wasTrackedUntil.checkout,
                summary: cartLocalStorageAtomState.wasTrackedUntil.summary,
            },
        };
        saveCartLocalStorageAtomState({ cart: updatedLocalStorageCart, storageKey });
    }, [cartLocalStorageAtomState, saveCartLocalStorageAtomState, storageKey]);

    const markCheckoutSummaryAsTracked = React.useCallback(() => {
        const updatedLocalStorageCart: LocalStorageCart = {
            ...cartLocalStorageAtomState,
            wasTrackedUntil: {
                cart: cartLocalStorageAtomState.wasTrackedUntil.cart,
                checkout: cartLocalStorageAtomState.wasTrackedUntil.checkout,
                summary: Date.now() + TRACKING_TTL.summary,
            },
        };
        saveCartLocalStorageAtomState({ cart: updatedLocalStorageCart, storageKey });
    }, [cartLocalStorageAtomState, saveCartLocalStorageAtomState, storageKey]);

    const markCheckoutAsTracked = React.useCallback(() => {
        const updatedLocalStorageCart: LocalStorageCart = {
            ...cartLocalStorageAtomState,
            wasTrackedUntil: {
                cart: cartLocalStorageAtomState.wasTrackedUntil.cart,
                checkout: Date.now() + TRACKING_TTL.checkout,
                summary: cartLocalStorageAtomState.wasTrackedUntil.summary,
            },
        };
        saveCartLocalStorageAtomState({ cart: updatedLocalStorageCart, storageKey });
    }, [cartLocalStorageAtomState, saveCartLocalStorageAtomState, storageKey]);

    const clearCart = React.useCallback(() => {
        clearCartLocalStorageAtomState(storageKey);
        triggerAppClearCart({ activitySpotId, schoolId });
    }, [activitySpotId, clearCartLocalStorageAtomState, schoolId, storageKey]);

    return {
        cartStorage: cartLocalStorageAtomState,
        clearCart,
        isCartReady,
        isProductInCart,
        markCartAsTracked,
        markCheckoutAsTracked,
        markCheckoutSummaryAsTracked,
        saveCartItems,
    };
}
